/**
 * The ball.
 *
 * The design is unusual and has to be understood before touching it. There is no separate
 * "live" integrator. Every tick the ball simulates a full three seconds ahead into an array
 * of 300 samples, and the live ball is simply what the first step of that simulation
 * produced. Every question the AI asks about the future — where will the ball be in 180 ms,
 * can I reach it, is it still in play when my foot arrives — is an O(1) lookup into that
 * array rather than a fresh simulation.
 *
 * That is also why a kick is visible to everyone immediately: `touch` re-runs the whole
 * prediction on the spot, so within the same tick every player's picture of the future is
 * already the new one.
 *
 * Source: ball.cpp:137-537 (`CalculatePrediction`), :91-135 (touch and spin), :562-585
 * (`Process`). The step order inside the loop is load-bearing and is the original's exactly:
 *
 *   gravity -> air drag -> grass bias -> bounce -> ground friction -> woodwork -> netting
 *   -> spin coupling -> magnus -> integrate
 *
 * THREE REPRODUCED QUIRKS of the original, none of them a bug in this port:
 *
 *   1. A fast-falling ball's recorded sample dips below ground level for a single 10 ms
 *      step before being clamped back, because the integration happens at the end of a step
 *      and the clamp at the start of the next. At 8 m/s that is about 8 cm.
 *   2. A resting ball never fully settles: gravity accumulates, the bounce fires, and the
 *      vertical momentum oscillates around 0.06-0.12 m/s indefinitely. Horizontal motion
 *      does stop completely, which is what actually matters to the game.
 *   3. Asking where the ball will be in zero milliseconds gives its position at the START
 *      of the tick, not where it is now — the forecast is built before the step is taken
 *      and the step is then adopted from it. Everything in the game is calibrated against
 *      that one-step lag, so removing it would shift every reach test by a centimetre or
 *      two of ball travel.
 *
 * The netting used to be a fourth quirk, written down here and in CLAUDE.md as dead code in
 * both games. That was wrong about the original — it sets the flag the moment its own goal
 * check fires (`ballIsInGoal`, match.cpp:974) — but right about this port, which never set it
 * at all, so the ball flew straight through the back of the goal. It is live now: `inGoal`
 * and `collideNetting` below.
 */

import { Quaternion, Vector3 } from 'three';
import { BALL, BALL_PREDICTION_STEPS, PITCH, TICK_MS } from '../core/constants';
import { clamp, normalizedClamp, normalizedOr, normalizedOrZero, signSide } from '../core/math';
import {
  angleAxis,
  angles,
  rotationAngle,
  rotationMultipliedBy,
  rotationTo,
  setAngles,
} from '../core/quat';

/** ball.cpp:159 — the prediction integrates at 100 Hz, the same rate as everything else. */
const TIME_STEP = TICK_MS / 1000;

/** gamedefines.hpp:277-279. */
export const GOAL = {
  height: 2.5,
  halfWidth: 3.7,
} as const;

/** ball.cpp:232-233. */
const POST_RADIUS = 0.07;
const POST_ABSORB_INV = 0.8;

/**
 * The goal netting. Read `collideNetting` before touching any of these.
 *
 * A net is not a wall and not a trampoline. It is a slack sheet that takes almost all of a
 * shot's pace out of it and gives back a little, so the ball bulges the net, stops, and drops
 * to the floor of the goal. These four numbers are what produce that, and they were tuned
 * against measurements of the extreme cases: a 30 m/s shot must not stretch the net much past
 * a third of a metre and must not come back out of the goal, and a ball rolled gently into it
 * must come to rest against it rather than rebound.
 */
const NET = {
  /**
   * How much of the speed INTO the net survives each 10 ms of contact. The original's 0.95
   * dissipates so little that a hard shot is fired back out of the goal at a third of the
   * pace it went in with — measured at 9 m/s back out for a 25 m/s shot, and 13 m/s for a
   * 35 m/s one. Netting absorbs; this is the number that makes it absorb.
   */
  absorb: 0.6,
  /**
   * How hard the stretched net pulls back, in metres per second per second per metre of
   * stretch, divided by 100. Only the netting's slackest part — the back of the goal, and
   * the sides away from the posts — is this soft.
   */
  stiffness: 5,
  /** Added to the above where the net is nailed to the woodwork and can barely give at all. */
  woodworkExtra: 5,
  /** How far the net can be stretched before it simply cannot give any more. */
  maxStretch: 1,
} as const;

const IDENTITY = new Quaternion();
const X_AXIS_NEG = new Vector3(-1, 0, 0);
const Y_AXIS = new Vector3(0, 1, 0);
const Z_AXIS = new Vector3(0, 0, 1);

/** Scratch, so a tick allocates nothing. The prediction loop runs 300 times per ball. */
const _normalized = new Vector3();
const _horizontal = new Vector3();
const _rotX = new Quaternion();
const _rotY = new Quaternion();
const _rotZ = new Quaternion();
const _groundRot = new Quaternion();
const _oldToNew = new Quaternion();
const _newRotation = new Quaternion();
const _rotVec = new Vector3();
const _swerve = new Vector3();
const _adaptedMomentum = new Vector3();
const _normal = new Vector3();
const _flat = new Vector3();
const _target = new Vector3();
const _outgoing = new Vector3();
const _stepRotation = new Quaternion();
const _touchTarget = new Quaternion();
const _predPos = new Vector3();
const _predMomentum = new Vector3();
const _predRotation = new Quaternion();
const _predOrient = new Quaternion();

export class Ball {
  /** Where the ball is right now. `positionBuffer` in the original. */
  readonly position = new Vector3(0, 0, BALL.radius);
  /** Metres per second. */
  readonly momentum = new Vector3();
  /** Spin, as a rotation per MILLISECOND. `rotation_ms` in the original. */
  readonly rotationMs = new Quaternion();
  /** Which way the ball's texture is facing. Cosmetic; drives nothing. */
  readonly orientation = new Quaternion();

  /**
   * Three seconds of future positions at 10 ms spacing, entry 0 being the start of the tick.
   *
   * Read it through `predict`, which takes milliseconds and clamps the way the AI expects.
   */
  readonly predictions: Vector3[] = Array.from(
    { length: BALL_PREDICTION_STEPS },
    () => new Vector3(0, 0, BALL.radius),
  );

  /**
   * True while the ball is in the goal, which is the whole switch for the netting.
   *
   * The ball works this out for itself, from its own position, in `updateGoalLatch`. It does
   * not ask the referee, and must not: the referee decides whether a GOAL is awarded, which
   * is a different question with a different answer — a ball that drops in over the crossbar
   * is in the goal and is not a goal, and a ball is still in the goal for as long as it sits
   * in the net after the whistle.
   */
  inGoal = false;
  /**
   * True while the ball is pressing on the netting, including a ball lying still against it.
   *
   * It is a state and not an event, so a sound hung straight off it would repeat for as long
   * as the ball sits in the net. What it is really for is the picture: the original pulls the
   * netting's own vertices towards the ball while this is true, hardest at the vertex nearest
   * to it and not at all where the net is nailed to the woodwork (`Match::UpdateGoalNetting`,
   * match.cpp:2159-2205). Nothing draws that here yet, so a hard shot briefly passes through
   * a rigid net before settling against it.
   */
  touchesNet = false;
  /** True for one tick when the ball struck a post or the bar, so audio can react. */
  hitWoodwork = false;

  private readonly orientPrediction = new Quaternion();
  private readonly firstMomentum = new Vector3();
  private readonly firstRotation = new Quaternion();
  /** Newest last, one sample per fixed tick. `ballPosHistory` in ball.cpp. */
  private readonly positionHistory: Vector3[] = [];

  constructor(position?: Vector3) {
    if (position) this.position.copy(position);
    this.positionHistory.push(this.position.clone());
    for (const p of this.predictions) p.copy(this.position);
    this.calculatePrediction();
  }

  /**
   * Where the ball will be `timeMs` from the start of this tick.
   *
   * Rounds down to the 10 ms grid. A time past the horizon gives the last sample; so does a
   * negative one, because the original's index is unsigned and wraps — reproduced rather
   * than corrected, since a caller relying on either is confused in the same way.
   */
  predict(timeMs: number, out = new Vector3()): Vector3 {
    let index = timeMs < 0 || timeMs >= BALL_PREDICTION_STEPS * 10 ? BALL_PREDICTION_STEPS - 1 : 0;
    if (timeMs >= 0 && timeMs < BALL_PREDICTION_STEPS * 10) index = Math.floor(timeMs / 10);
    return out.copy(this.predictions[index]!);
  }

  /** Metres per second. `Ball::GetMovement`. */
  movement(out = new Vector3()): Vector3 {
    return out.copy(this.momentum);
  }

  /**
   * One 10 ms tick — `Ball::Process` (ball.cpp:562-585).
   *
   * Note what this does NOT do: integrate. It runs the prediction and then adopts the
   * prediction's first step as the new present. The live ball and the forecast therefore
   * cannot disagree, which is the whole reason the AI is allowed to trust the forecast.
   */
  process(): void {
    this.calculatePrediction();
    this.momentum.copy(this.firstMomentum);
    this.rotationMs.copy(this.firstRotation);
    this.predict(10, this.position);
    this.orientation.copy(this.orientPrediction);
    this.positionHistory.push(this.position.clone());
    if (this.positionHistory.length > 401) this.positionHistory.shift();
  }

  /** Mean of the recent live samples, not of the future forecast. `Ball::GetAveragePosition`. */
  averagePosition(durationMs: number, out = new Vector3()): Vector3 {
    out.set(0, 0, 0);
    let count = 0;
    for (let i = this.positionHistory.length - 1; i >= 0; i--) {
      out.add(this.positionHistory[i]!);
      count++;
      if (count * TICK_MS > durationMs) break;
    }
    return count > 0 ? out.divideScalar(count) : out.copy(this.position);
  }

  /**
   * Strike the ball — `Ball::Touch` (ball.cpp:91-103).
   *
   * The momentum is REPLACED, not added to: whatever the touch decided is the new movement
   * in full. Callers blend in the old movement themselves when a touch was mistimed, which
   * is what makes a scuffed trap keep some of the ball's original pace.
   */
  touch(target: Vector3): void {
    if (this.position.z < BALL.radius) this.position.z = BALL.radius;
    this.momentum.copy(target);
    this.calculatePrediction();
  }

  /**
   * Add spin, in radians per second about each axis, blended onto the existing spin.
   *
   * `bias` of 1 replaces the spin outright, 0 leaves it alone. Every touch type picks its
   * own: a pass uses 0.9 because a struck ball's spin is entirely the striker's doing, a
   * deflection uses 0.2 because it mostly keeps whatever it had.
   */
  setRotation(x: number, y: number, z: number, bias: number): void {
    const limit = Math.PI * 0.49;
    angleAxis(clamp(x * 0.001, -limit, limit), X_AXIS_NEG, _rotX);
    angleAxis(clamp(y * 0.001, -limit, limit), Y_AXIS, _rotY);
    angleAxis(clamp(z * 0.001, -limit, limit), Z_AXIS, _rotZ);
    _touchTarget.copy(_rotX).multiply(_rotY).multiply(_rotZ);
    this.rotationMs.slerp(_touchTarget, bias);
    this.calculatePrediction();
  }

  /** Put the ball somewhere and stop it dead. */
  setPosition(target: Vector3): void {
    this.position.copy(target);
    this.momentum.set(0, 0, 0);
    this.rotationMs.set(0, 0, 0, 1);
    this.positionHistory.length = 0;
    this.positionHistory.push(this.position.clone());
    this.calculatePrediction();
  }

  /**
   * Fill the prediction array, and record what the state will be after the first step.
   *
   * This is the whole of the ball's physics. It runs once per tick per ball, plus once more
   * on every touch, and it is 300 iterations each time — comfortably the most expensive
   * single thing in the simulation.
   */
  private calculatePrediction(): void {
    const nextPos = _predPos.copy(this.position);
    const nextOrientation = _predOrient.copy(this.orientation);
    const momentum = _predMomentum.copy(this.momentum);
    const rotation = _predRotation.copy(this.rotationMs);

    this.predictions[0]!.copy(this.position);
    this.touchesNet = false;
    this.hitWoodwork = false;
    // Reading the ball's position, so re-running it within a tick — which a touch does —
    // cannot change the answer, and moving the ball re-decides it from where it now is.
    this.updateGoalLatch();

    for (let step = 1; step < BALL_PREDICTION_STEPS; step++) {
      let frictionFactor = 0;

      // --- gravity (ball.cpp:174) ---
      momentum.z += BALL.gravity * TIME_STEP;

      // --- air resistance (ball.cpp:179-183) ---
      // Quadratic drag applied to the speed, then re-projected onto the direction.
      const speed = momentum.length();
      const dragged = speed - BALL.drag * speed * speed * TIME_STEP;
      normalizedOrZero(momentum, _normalized);
      momentum.copy(_normalized).multiplyScalar(dragged);

      // --- how deep the ball is sitting in the grass (ball.cpp:186-192) ---
      // Computed BEFORE the bounce clamps the height — do not move this below the bounce.
      const ballBottom = nextPos.z - BALL.radius;
      const grassBias = Math.pow(
        clamp(1 - ballBottom / BALL.grassHeight, 0, 1),
        0.7, // at half the grass depth there is already more than half the friction
      );

      // --- bounce (ball.cpp:196-207) ---
      if (nextPos.z < BALL.radius) {
        if (momentum.z < 0) {
          // A ball slammed into the ground picks up more friction and far more spin. Set
          // only here, so it is computed exactly once per bounce.
          frictionFactor = normalizedClamp(-momentum.z - 0.5, 0, 12);
          momentum.z = -momentum.z * BALL.bounce;
          momentum.z = Math.max(momentum.z - BALL.linearBounce, 0);
        }
        nextPos.z = BALL.radius;
      }

      // --- ground friction (ball.cpp:210-227) ---
      if (nextPos.z < BALL.radius + BALL.grassHeight) {
        const adaptedFriction = BALL.friction * grassBias;
        _horizontal.set(momentum.x, momentum.y, 0);
        const velo = _horizontal.length();
        let newVelo = velo - adaptedFriction * velo * velo * TIME_STEP;
        newVelo = clamp(newVelo - BALL.linearFriction * grassBias * TIME_STEP, 0, 100000);
        normalizedOrZero(_horizontal, _normalized).multiplyScalar(newVelo);
        momentum.x = _normalized.x;
        momentum.y = _normalized.y;
      }

      // --- woodwork and netting, first step only (ball.cpp:238-408) ---
      if (step === 1) {
        this.collideWoodwork(nextPos, momentum);
        this.collideNetting(nextPos, momentum);
      }

      // --- spin (ball.cpp:411-481) ---
      if (nextPos.z < BALL.radius + BALL.grassHeight) {
        // Rolling in one horizontal direction is a rotation about the OTHER axis, which is
        // why x and y look swapped here and below. They are not.
        const xR = momentum.y / BALL.radius;
        const yR = momentum.x / BALL.radius;

        const limit = Math.PI * 0.49;
        angleAxis(clamp(xR * 0.001, -limit, limit), X_AXIS_NEG, _rotX);
        angleAxis(clamp(yR * 0.001, -limit, limit), Y_AXIS, _rotY);
        _groundRot.copy(_rotX).multiply(_rotY);

        // How fast the spin would have to change to match the rolling speed, and a cap on
        // how fast it is allowed to. The cap is what makes a struck ball skid before it
        // grips; a hard bounce lifts the cap enormously, so it grips almost at once.
        rotationTo(rotation, _groundRot, _oldToNew).normalize();
        const changePerSecond = Math.abs(rotationAngle(_oldToNew, IDENTITY)) * 1000;
        let maxChangePerSecond = Math.PI * grassBias;
        if (frictionFactor > 0) maxChangePerSecond += 4 * Math.PI;
        if (changePerSecond > maxChangePerSecond) {
          rotationMultipliedBy(_oldToNew, maxChangePerSecond / changePerSecond, _oldToNew);
        }
        _newRotation.copy(_oldToNew).multiply(rotation);

        // ...and the other half of the coupling: the spin drags the ball along with it.
        angles(rotation, _rotVec);
        const rollDrivenX = _rotVec.y * BALL.radius * 1000;
        const rollDrivenY = -_rotVec.x * BALL.radius * 1000;

        let rotBias = 0.01 * grassBias; // lower = ball feels lighter, higher = more rubbery
        if (frictionFactor > 0) rotBias += 0.5 * frictionFactor;
        rotBias = clamp(rotBias, 0, 1);
        momentum.x = momentum.x * (1 - rotBias) + rollDrivenX * rotBias;
        momentum.y = momentum.y * (1 - rotBias) + rollDrivenY * rotBias;

        rotation.copy(_newRotation);
      }

      // --- magnus effect, the swerve on a spinning ball (ball.cpp:484-502) ---
      //
      // The order of the cross product below is the entire direction of every curved ball
      // in the game, and swapping its two operands silently negates it. The original is
      // `adaptedMomentumPredict.GetCrossProduct(-rotVec)` (ball.cpp:498) — movement first,
      // negated spin second. In plain terms: a ball running up the pitch whose spin axis
      // points straight up is pushed to its own left, and a ball struck with topspin is
      // pushed down. Reversed, it still compiles, still curves, and bends every cross,
      // curled pass and dipping shot to the wrong flank. Pinned by tests/ball.test.ts.
      //
      // The strength is deliberately not proportional to speed: it peaks near 37 m/s and
      // then collapses, so a ball smashed at 68 m/s barely swerves at all. The original's
      // author says so in a comment.
      angles(rotation, _rotVec).multiplyScalar(10);
      let swerveAmount = normalizedClamp(momentum.length(), 0, 70);
      swerveAmount = Math.pow(Math.sin(swerveAmount * Math.PI * 0.94), 2.6);
      normalizedOrZero(momentum, _adaptedMomentum).multiplyScalar(swerveAmount * 30);
      _swerve.copy(_adaptedMomentum).cross(_rotVec.negate());
      momentum.addScaledVector(_swerve, TIME_STEP);

      // --- integrate (ball.cpp:506-514) ---
      nextPos.addScaledVector(momentum, TIME_STEP);

      angles(rotation, _rotVec).multiplyScalar(TIME_STEP / 0.001);
      setAngles(_rotVec.x, _rotVec.y, _rotVec.z, _stepRotation);
      nextOrientation.premultiply(_stepRotation);

      this.predictions[step]!.copy(nextPos);

      if (step === 1) {
        this.firstMomentum.copy(momentum);
        this.firstRotation.copy(rotation);
        this.orientPrediction.copy(nextOrientation);
      }
    }
  }

  /**
   * Posts and crossbar (ball.cpp:240-328).
   *
   * The reflection is deliberately NOT a mirror: the outgoing direction is the incoming one
   * blended with 1.1 times the surface normal, which biases every post hit outwards, away
   * from the goal. That is why balls off the woodwork in this game so rarely come back in.
   */
  private collideWoodwork(nextPos: Vector3, momentum: Vector3): void {
    const half = PITCH.halfLength;
    const goalHalf = GOAL.halfWidth;

    // --- posts ---
    _flat.set(Math.abs(nextPos.x) - half, Math.abs(nextPos.y) - goalHalf, 0);
    if (
      nextPos.z < GOAL.height + BALL.radius + POST_RADIUS &&
      _flat.length() < BALL.radius + POST_RADIUS
    ) {
      const postX = signSide(nextPos.x) * half;
      const postY = signSide(nextPos.y) * goalHalf;
      _target.set(nextPos.x < 0 ? 1 : -1, 0, 0);
      normalizedOr(_flat.set(nextPos.x - postX, nextPos.y - postY, 0), _target, _normal);

      const keepZ = nextPos.z;
      nextPos.set(postX, postY, 0).addScaledVector(_normal, POST_RADIUS + BALL.radius);
      nextPos.z = keepZ;

      _flat.set(momentum.x, momentum.y, 0);
      const speed = _flat.length();
      normalizedOr(_flat, _normal, _outgoing).addScaledVector(_normal, 1.1).normalize();
      momentum.x = _outgoing.x * speed * POST_ABSORB_INV;
      momentum.y = _outgoing.y * speed * POST_ABSORB_INV;
      this.hitWoodwork = true;
    }

    // --- crossbar ---
    _flat.set(Math.abs(nextPos.x) - half, 0, Math.abs(nextPos.z) - GOAL.height);
    if (
      _flat.length() < BALL.radius + POST_RADIUS &&
      Math.abs(nextPos.y) < goalHalf + BALL.radius + POST_RADIUS
    ) {
      const barX = signSide(nextPos.x) * half;
      _target.set(nextPos.x < 0 ? 1 : -1, 0, 0);
      normalizedOr(_flat.set(nextPos.x - barX, 0, nextPos.z - GOAL.height), _target, _normal);

      const keepY = nextPos.y;
      nextPos.set(barX, 0, GOAL.height).addScaledVector(_normal, POST_RADIUS + BALL.radius);
      nextPos.y = keepY;

      _flat.set(momentum.x, 0, momentum.z);
      const speed = _flat.length();
      normalizedOr(_flat, _normal, _outgoing).addScaledVector(_normal, 1.1).normalize();
      momentum.x = _outgoing.x * speed * POST_ABSORB_INV;
      momentum.z = _outgoing.z * speed * POST_ABSORB_INV;
      this.hitWoodwork = true;
    }
  }

  /**
   * Decide whether the ball is in the goal, from nothing but where the ball is.
   *
   * It has to be a latch rather than a plain test of the box, because the states in which
   * the netting matters most are the ones where the ball is NOT in the box: pressing into
   * the side net at 3.9 m across, or bulging the back of the goal out past 57.8 m. So the
   * ball is in the goal once it is fully over the line between the posts and under the bar,
   * and it stays in the goal until it comes back out over that line — or gets further from
   * the goal than the netting could ever stretch, which is only reachable by being picked up
   * and put down somewhere else.
   *
   * Note what this deliberately does NOT do: award a goal. A ball dropping in over the
   * crossbar is in the goal and is not a goal, and after the whistle the ball is still in
   * the goal for as long as it lies in the net. The referee's own test is a crossing of the
   * goal-mouth plane and is a separate thing.
   */
  private updateGoalLatch(): void {
    const half = PITCH.halfLength;
    const x = Math.abs(this.position.x);
    const y = Math.abs(this.position.y);
    const z = this.position.z;

    const insideTheGoal =
      x > half + BALL.radius &&
      x < half + PITCH.goalDepth &&
      y < GOAL.halfWidth &&
      z < GOAL.height;
    if (insideTheGoal) {
      this.inGoal = true;
      return;
    }

    // Still in there, with the netting stretched around it. The near edge is the goal line
    // itself and has no allowance, so a ball rolling back out is out the moment it crosses.
    const stretchingTheNet =
      x > half &&
      x < half + PITCH.goalDepth + NET.maxStretch &&
      y < GOAL.halfWidth + NET.maxStretch &&
      z < GOAL.height + NET.maxStretch;
    this.inGoal = this.inGoal && stretchingTheNet;
  }

  /**
   * The goal netting (ball.cpp:333-408).
   *
   * Structurally this is still the original's: three sheets, each an independent spring
   * acting along its own axis and damping only the speed into itself, so a ball that hits
   * the back of the goal carries on sliding sideways and carries on falling.
   *
   * The arithmetic is not. This code had never run here, and in the original it can only run
   * during the few seconds between a goal being given and the restart, so it is effectively
   * untested in both. Read carefully it has four faults, and switching it on as written puts
   * the ball two metres out through the back of the goal and then fires it back onto the
   * penalty spot. All four are corrected:
   *
   *   - FIXED: how far the net is stretched was measured from the plane the net hangs in,
   *     not from where the ball first touches it. The pull-back was therefore ZERO at the
   *     moment the ball reached the netting and only grew once the ball was already through
   *     it, which is most of the reason a shot sailed out of the back of the goal.
   *   - FIXED: the top sheet only noticed a ball once it was a full ball-width above the
   *     crossbar's height, where the two side sheets and the back correctly notice one as
   *     soon as it touches. So a ball could pass up through the roof of the net.
   *   - FIXED: how taut the net is was read off the ball's MOMENTUM where it means the
   *     ball's POSITION (`fabs(momentumPredict.coords[0]) - pitchHalfW`, ball.cpp:361). A
   *     ball never travels at 55 m/s, so the whole net was permanently treated as if it were
   *     nailed to a post. The same expression is written out correctly, off the position,
   *     where the original deforms the drawn netting (match.cpp:2183).
   *   - FIXED: the pull-back was far too weak and far too springy for netting. Measured with
   *     the original's numbers: a shot into the back of the goal at 25 m/s came back out over
   *     the goal line two thirds of a second later, still doing 9 m/s. See `NET`.
   *
   * Not fixed, and worth knowing: this runs on the first prediction step only, exactly as
   * the woodwork does, so the three-second forecast still shows the ball sailing through the
   * back of the goal. Nothing reads that forecast while the ball is in the net — play has
   * been stopped by then — and running it over all 300 steps would need the latch above
   * evaluated per step, in the most expensive loop in the simulation.
   *
   * Also not fixed: the netting only exists for a ball INSIDE the goal. A ball that goes
   * over the bar and drops steeply behind it passes through the roof of the net from above.
   * The original's author left the outside-in cases commented out with "too hard to code",
   * and they need the ball to know which side of each sheet it is on — which the latch above
   * now provides, so this is doable, but it is a separate piece of work.
   */
  private collideNetting(nextPos: Vector3, momentum: Vector3): void {
    if (!this.inGoal) return;

    const half = PITCH.halfLength;
    // In front of the goal line the ball is in the mouth of the goal, where there is nothing.
    if (Math.abs(nextPos.x) < half + BALL.radius) return;

    const absorb = Math.pow(NET.absorb, TIME_STEP * 100);
    // The netting is nailed to the woodwork, so it hardly gives at all beside a post or
    // under the bar, and is at its slackest half a metre further back.
    const slack = clamp((Math.abs(nextPos.x) - half) * 2, 0, 1);
    const nailed = NET.stiffness + (1 - slack) * NET.woodworkExtra;

    const intoSide = Math.abs(nextPos.y) - (GOAL.halfWidth - BALL.radius);
    if (intoSide > 0) {
      const stretch = Math.min(intoSide, NET.maxStretch);
      momentum.y = momentum.y * absorb - signSide(nextPos.y) * stretch * nailed * (100 * TIME_STEP);
      this.touchesNet = true;
    }

    const intoBack = Math.abs(nextPos.x) - (half + PITCH.goalDepth - BALL.radius);
    if (intoBack > 0) {
      const stretch = Math.min(intoBack, NET.maxStretch);
      momentum.x =
        momentum.x * absorb - signSide(nextPos.x) * stretch * NET.stiffness * (100 * TIME_STEP);
      this.touchesNet = true;
    }

    const intoRoof = nextPos.z - (GOAL.height - BALL.radius);
    if (intoRoof > 0) {
      const stretch = Math.min(intoRoof, NET.maxStretch);
      momentum.z = momentum.z * absorb - stretch * nailed * (100 * TIME_STEP);
      this.touchesNet = true;
    }
  }
}
